import { Button, Stack, Tile, Typography, useTheme } from "@carrotfertility/carotene-core"
import React from "react"
import { perViewport } from "./responsive"

/**
 * Data inside the body sentence, not copy. `OtherWaysToContactCarrotTile` in the host reads the same
 * sentence's number from `useGetPhoneSupportNumberForUser()`; this page has no data wiring yet, so
 * the value stays literal here. CONAPP-275 replaces it with the real per-member number.
 *
 * The sentence has no trailing period, matching the host tile it mirrors, even though `.rail-b` draws
 * one. See CONAPP-274.
 */
const CARROT_COMPANIONS_PHONE_NUMBER = "+1 888-817-9040"

/**
 * The "Other ways to contact Carrot" rail — `.rail-note` in every frame of S4.
 *
 * Built on `Tile variant="secondary" color="green"` with the content laid out by hand, rather than on
 * `Card`. `Card` looked like the closer fit and was not: it hardcodes its title to `variant="h5"`
 * (18px bold) where `.rail-t` is **h4** (22px, w400), and its internal `Stack`s are fixed at
 * `sm`/`lg`, giving 12px/12px/24px against the design's 16px/8px/16px. Neither is reachable through
 * props.
 *
 * This is also the shape `OtherWaysToContactCarrotTile` already ships for the same rail elsewhere in
 * the app — same primitive, same `h4`, same nesting — so matching the design here and matching the
 * app's own realisation of it turned out to be the same change. The 8px `brandGreen[200]` bar and the
 * 24px padding come from the Carotene tile untouched; `.rail-bar` draws 6px of `--brand-green-300`,
 * a DS-vs-canvas delta left alone deliberately (CONAPP-274).
 */
export function SidebarCard(): JSX.Element {
  const theme = useTheme()

  return (
    <Tile variant="secondary" color="green">
      <Stack sx={{ flexDirection: "column" }} spacing={theme.spacing(theme.tokens.spacing.md)}>
        <Typography variant="h4" component="h2">
          Other ways to contact Carrot
        </Typography>
        {/* `.rail-k` and `.rail-b` sit 8px apart, closer than the 16px separating the other blocks. */}
        <Stack sx={{ flexDirection: "column" }} spacing={theme.spacing(theme.tokens.spacing.xs)}>
          <Typography variant="h6" component="h3">
            Call us
          </Typography>
          <Typography>
            Need help now? Talk to one of our Carrot Companions at {CARROT_COMPANIONS_PHONE_NUMBER}
          </Typography>
        </Stack>
        {/*
          `.mob-btn { width: 100% }` — frame 9 makes every mobile CTA full width, and this one was the
          last on the page still shrink-wrapping: `Card` put its `primaryAction` in a
          `flex-direction: row; flex-wrap: wrap` box that never stretches, so the Up next and booking
          CTAs stretched and this one did not. Set on the button rather than by stretching the column,
          because the two `Typography` blocks above it must keep their own measure.
        */}
        <Button variant="outlined" color="secondary" sx={{ inlineSize: perViewport("100%", "fit-content") }}>
          Call us
        </Button>
      </Stack>
    </Tile>
  )
}
